import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, isLoggedIn } from '@/lib/auth';
import { ensurePushSubscription } from '@/lib/push';
import { startStandby, stopStandby, isRingModeEnabled, primeAudio } from '@/lib/sounds';

export const RING_MODE_EVENT = 'dl-ringmode';          // fired when settings/permission change
const AUTO_SETUP_KEY = 'dl_auto_setup_v1';
const OWNER_PAGES = ['/messages', '/history', '/plans', '/label'];

const permission = () => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

// App-wide "DotLink is ready" behaviour, so it doesn't depend on which page is open:
//  1. Ring mode (inaudible keep-alive audio) runs on EVERY page while logged in
//     with alerts granted — on by default, switchable in the dashboard.
//  2. First tap after login sets up alerts automatically (audio unlock,
//     notification permission, push subscription, persistent storage).
//  3. If a call arrives while the owner is on another page (messages, history…)
//     jump straight to the dashboard, where the incoming-call screen lives.
const RingModeManager = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // (1) keep standby running / stopped according to login + permission + setting
  useEffect(() => {
    const apply = () => {
      const on = isLoggedIn() && isRingModeEnabled() && permission() === 'granted';
      if (on) startStandby(); else stopStandby();
    };
    apply();
    window.addEventListener(RING_MODE_EVENT, apply);
    window.addEventListener('storage', apply);
    return () => {
      window.removeEventListener(RING_MODE_EVENT, apply);
      window.removeEventListener('storage', apply);
    };
  }, [pathname]);

  // (2) one-time automatic setup on the first tap
  useEffect(() => {
    if (!isLoggedIn()) return undefined;
    let done = false;
    try { done = localStorage.getItem(AUTO_SETUP_KEY) === '1'; } catch { /* ignore */ }

    if (permission() === 'granted') {
      ensurePushSubscription().catch(() => {});        // keep this device registered
      return undefined;
    }
    if (done || permission() !== 'default') return undefined;

    const setup = async () => {
      try { localStorage.setItem(AUTO_SETUP_KEY, '1'); } catch { /* ignore */ }
      primeAudio();
      try { await navigator.storage?.persist?.(); } catch { /* ignore */ }
      try { await Notification.requestPermission(); } catch { /* ignore */ }
      ensurePushSubscription().catch(() => {});
      window.dispatchEvent(new Event(RING_MODE_EVENT));
    };
    document.addEventListener('click', setup, { once: true });
    return () => document.removeEventListener('click', setup);
  }, [pathname]);

  // (3) a call on any owner page -> go to the dashboard
  useEffect(() => {
    if (!isLoggedIn() || !OWNER_PAGES.some((p) => pathname.startsWith(p))) return undefined;
    let stopped = false;
    const check = async () => {
      try {
        const res = await api.get('/call/owner/pending');
        if (!stopped && (res.data.count || 0) > 0) navigate('/dashboard');
      } catch { /* ignore */ }
    };
    const timer = setInterval(check, 2000);
    const onSw = (e) => { if (e.data?.type === 'incoming-call') check(); };
    navigator.serviceWorker?.addEventListener('message', onSw);
    return () => {
      stopped = true;
      clearInterval(timer);
      navigator.serviceWorker?.removeEventListener('message', onSw);
    };
  }, [pathname, navigate]);

  return null;
};

export default RingModeManager;
